import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function AdminRoles() {
    const [roles, setRoles] = useState([]);
    const [permsByCat, setPermsByCat] = useState({});
    const [matrix, setMatrix] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [r, p, m] = await Promise.all([
                api.get("/roles"),
                api.get("/permissions"),
                api.get("/permissions/matrix"),
            ]);
            setRoles(r.data);
            setPermsByCat(p.data.grouped);
            setMatrix(m.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const editableRoles = useMemo(
        () => roles.filter((r) => r.code !== "super_admin"),
        [roles],
    );

    const toggle = (roleCode, permCode) => {
        if (roleCode === "super_admin") return;
        setMatrix((m) => {
            const current = new Set(m[roleCode] || []);
            if (current.has(permCode)) current.delete(permCode);
            else current.add(permCode);
            return { ...m, [roleCode]: Array.from(current) };
        });
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {};
            for (const r of editableRoles) {
                payload[r.code] = matrix[r.code] || [];
            }
            await api.put("/permissions/matrix", { matrix: payload });
            toast.success("Permission matrix saved");
            setDirty(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-white/60 p-8 text-center">Loading…</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">RBAC</div>
                    <h1 className="font-serif text-4xl text-white">Roles & Permissions</h1>
                    <p className="text-white/60 text-sm mt-2">
                        Grant or revoke permission codes for each role. Super-admin has all
                        permissions implicitly.
                    </p>
                </div>
                <button
                    data-testid="roles-save-matrix"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="lx-btn-primary px-5 py-2.5 disabled:opacity-40"
                >
                    {saving ? "Saving…" : dirty ? "Save matrix" : "No changes"}
                </button>
            </div>

            {Object.entries(permsByCat).map(([cat, perms]) => (
                <div key={cat} className="lx-card-dark p-0 overflow-x-auto">
                    <div className="px-4 py-3 bg-[#2B1438]/40 border-b border-[#2B1438]">
                        <div className="lx-label text-[#D4AF37]">{cat}</div>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-left text-white/60 border-b border-[#2B1438]">
                            <tr>
                                <th className="p-3 lx-label">Permission</th>
                                {editableRoles.map((r) => (
                                    <th
                                        key={r.code}
                                        className="p-3 lx-label text-center min-w-[110px]"
                                    >
                                        {r.label || r.code}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {perms.map((p) => (
                                <tr
                                    key={p.code}
                                    className="border-b border-[#2B1438]"
                                    data-testid={`perm-row-${p.code}`}
                                >
                                    <td className="p-3">
                                        <div className="text-white font-mono text-xs">
                                            {p.code}
                                        </div>
                                        <div className="text-white/50 text-xs">
                                            {p.description}
                                        </div>
                                    </td>
                                    {editableRoles.map((r) => {
                                        const granted = (matrix[r.code] || []).includes(p.code);
                                        return (
                                            <td key={r.code} className="p-3 text-center">
                                                <button
                                                    data-testid={`perm-${p.code}-${r.code}`}
                                                    onClick={() => toggle(r.code, p.code)}
                                                    className={`w-7 h-7 rounded-sm border ${
                                                        granted
                                                            ? "bg-[#D4AF37] border-[#D4AF37] text-[#180A21]"
                                                            : "bg-transparent border-[#2B1438] text-white/30"
                                                    }`}
                                                    title={granted ? "Granted" : "Not granted"}
                                                >
                                                    {granted ? "✓" : ""}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}
